import { useState, type FormEvent } from 'react'
import {
  Alert, Box, Button, Card, CardContent, IconButton, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import { Languages } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { Language } from '@mefosa/shared'
import { useAuth } from '../auth/AuthProvider'
import { ApiRequestError } from '../api/client'
import { BRAND } from '../theme'

export default function LoginPage() {
  const { t, i18n } = useTranslation()
  const { signIn, setLanguage } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const current = (i18n.language === 'ar' ? 'ar' : 'en') as Language

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await signIn(email, password)
    } catch (err) {
      if (err instanceof ApiRequestError) {
        // Only a 401 means the credentials are wrong. Telling a rate-limited
        // or offline user their password is wrong sends them down a dead end.
        setError(
          err.code === 'NETWORK' ? t('errors.network')
            : err.status === 401 ? t('auth.invalid')
              : err.message,
        )
      } else {
        setError(t('errors.generic'))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        p: 2,
        background: `linear-gradient(160deg, ${BRAND.navy} 0%, #0A2545 55%, #071B33 100%)`,
      }}
    >
      <Box sx={{ position: 'absolute', insetBlockStart: 16, insetInlineEnd: 16 }}>
        <Tooltip title={t('language.switch')}>
          <IconButton
            onClick={() => void setLanguage(current === 'en' ? 'ar' : 'en')}
            sx={{ color: '#fff' }}
            aria-label={t('language.switch')}
          >
            <Languages size={20} />
          </IconButton>
        </Tooltip>
      </Box>

      <Card sx={{ width: '100%', maxWidth: 420 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box
            component="img"
            src="/brand/logo.webp"
            alt="MEFOSA — MENA Food Safety Associates"
            sx={{ width: '100%', maxWidth: 260, display: 'block', mx: 'auto', mb: 3 }}
          />

          <Typography variant="h6" component="h1" sx={{ textAlign: 'center' }}>
            {t('app.name')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
            {t('auth.welcome')}
          </Typography>

          <form onSubmit={onSubmit} noValidate>
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}

              <TextField
                label={t('auth.email')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                autoFocus
                required
                fullWidth
              />
              <TextField
                label={t('auth.password')}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                fullWidth
              />
              <Button type="submit" variant="contained" size="large" disabled={busy} fullWidth>
                {busy ? t('auth.signingIn') : t('auth.signIn')}
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  )
}
